"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Check, Pencil, Plus } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { FieldChevron } from "@/components/ui/select";
import { DOCTOR_SPECIALTY_I18N_KEYS, isDoctorSpecialty, SPECIALTY_MAX, specialtyMenu, } from "@/lib/doctor-profile";
import { cn } from "@/lib/utils";
const ROW = "-mx-2 flex h-7 cursor-pointer items-center gap-2 rounded-[8px] px-2 text-body text-ink transition-colors duration-hover hover:bg-row-hover";
/**
 * Edit profile → Specialty (C2, C2.1): type to filter, pick from the list, «Other · Type your own»
 * or «Add “…”» for a specialty of the doctor's own. The backend checks the same length.
 */
export function SpecialtyField({ value, onChange, }) {
    const t = useTranslations("settings");
    const [open, setOpen] = useState(false);
    const [typing, setTyping] = useState(false);
    const [query, setQuery] = useState("");
    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const labelOf = (specialty) => t(`specialties.${DOCTOR_SPECIALTY_I18N_KEYS[specialty]}`);
    const currentLabel = !value
        ? ""
        : isDoctorSpecialty(value)
            ? labelOf(value)
            : value;
    const menu = specialtyMenu(value, query, labelOf);
    const close = () => {
        setOpen(false);
        setTyping(false);
        setQuery("");
    };
    const choose = (next) => {
        var _a;
        onChange(next);
        close();
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
    };
    useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            var _a;
            if (!((_a = boxRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                setOpen(false);
                setTyping(false);
                setQuery("");
            }
        };
        // Esc closes the menu, not the dialog around it: the dialog listens on document in the
        // capture phase, so this has to catch it on window, before that
        const onEsc = (e) => {
            var _a;
            if (e.key !== "Escape")
                return;
            e.stopPropagation();
            e.preventDefault();
            setOpen(false);
            setTyping(false);
            setQuery("");
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        };
        document.addEventListener("pointerdown", onDown);
        window.addEventListener("keydown", onEsc, true);
        return () => {
            document.removeEventListener("pointerdown", onDown);
            window.removeEventListener("keydown", onEsc, true);
        };
    }, [open]);
    return (_jsxs("div", { ref: boxRef, className: "relative", children: [_jsxs("div", { className: cn("flex h-10 cursor-text items-center gap-2 rounded-control border px-3.5 transition-colors duration-hover hover:border-control-hover", open ? "border-control-hover bg-well" : "border-control"), children: [_jsx("input", { ref: inputRef, value: open ? query : currentLabel, maxLength: SPECIALTY_MAX, placeholder: typing
                            ? t("specialtyTypePlaceholder")
                            : open && currentLabel
                                ? currentLabel
                                : t("chooseSpecialty"), onFocus: () => setOpen(true), onChange: (e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }, onKeyDown: (e) => {
                            var _a, _b;
                            if (e.key === "Enter") {
                                // Inside the profile form: Enter picks here, it doesn't save the form
                                e.preventDefault();
                                if (menu.typed)
                                    choose((_b = (_a = menu.match) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : menu.typed);
                            }
                        }, className: "h-full min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-muted" }), _jsx("button", { type: "button", "aria-label": t("specialty"), tabIndex: -1, onClick: () => {
                            var _a;
                            if (open)
                                close();
                            else
                                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        }, className: "inline-flex shrink-0 cursor-pointer text-ink-muted", children: _jsx(FieldChevron, { className: cn("transition-transform duration-hover", open && "rotate-180") }) })] }), open ? (_jsxs("div", { className: "absolute inset-x-0 top-[calc(100%+8px)] z-10 flex flex-col gap-0.5 rounded-control bg-surface p-4 shadow-popover", children: [menu.options.map((option) => {
                        const on = option.value === value;
                        return (_jsxs("button", { type: "button", onClick: () => choose(option.value), className: cn(ROW, "text-left", on ? "bg-row-hover font-semibold" : "font-medium"), children: [_jsx("span", { className: "min-w-0 flex-1 truncate", children: option.label }), on ? _jsx(Check, { className: "size-4 shrink-0" }) : null] }, option.value));
                    }), !menu.typed ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "my-1.5 h-px bg-line" }), _jsxs("button", { type: "button", onClick: () => {
                                    var _a;
                                    setTyping(true);
                                    setQuery("");
                                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                }, className: cn(ROW, "text-left font-medium"), children: [_jsx(Pencil, { className: "size-4 shrink-0" }), _jsx("span", { className: "min-w-0 flex-1", children: t("specialties.other") }), _jsx("span", { className: "shrink-0 text-caption text-ink-2", children: t("specialtyTypeOwn") })] })] })) : null, menu.canAdd ? (_jsxs(_Fragment, { children: [menu.options.length > 0 ? (_jsx("div", { className: "my-1.5 h-px bg-line" })) : null, _jsxs("button", { type: "button", onClick: () => choose(menu.typed), className: cn(ROW, "text-left"), children: [_jsx(Plus, { className: "size-4 shrink-0" }), _jsx("span", { className: "min-w-0 flex-1 truncate", children: t("specialtyAdd", { value: menu.typed }) })] })] })) : null] })) : null] }));
}
